import {
    IonDatetime,
    IonDatetimeButton,
    IonItem,
    IonLabel,
    IonList,
    IonModal,
    IonSelect,
    IonSelectOption,
    IonToggle,
} from "@ionic/react";
import { useAppContext } from "State/Global/AppContext";
import React, { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import CTA from "Constants";
import { Agent } from "frontend-lib/DomainTypes/User";
import { ConversationLabel } from "frontend-lib/DomainTypes/Conversation";
import moment from "moment";

// TODO: this definition is duplicated. Remove this and use the one in chatlistfunction.ts
interface Filters {
    status: "ENDED" | "ACTIVE" | "INIT_BY_AGENT";
    customerChannel:
        | "OPTIWE_CHAT"
        | "WHATSAPP"
        | "INSTAGRAM"
        | "FACEBOOK"
        | "SMS"
        | "FACEBOOK_COMMENT";
    chatGroupsId: Array<number> | null;
    isWaiting: boolean;
    sortBy: "START_WAITING_ON" | "LAST_END_USER_ACTIVITY" | null;
    participatingAgentsId: Array<number> | null;
    conversationLabels: Array<number> | null;
    startDatetime: string;
    endDatetime: string;
    notServed: boolean;
}

interface IonFiltersMobileProps {
    filters: Filters;
    handlers: {
        handleChangeChatGroup: (groupsId: Array<number>) => void;
        handleChangeChannel: (channel: string) => void;
        handleWaitingFilter: () => void;
        handleChangeSortBy: (sorting: string) => void;
        handleSelectAgents: (agentsId: Array<number>) => void;
        handleSelectConversationLabels: (labelsId: Array<number>) => void;
        handleDateRangeChange: (date: unknown, dateString: [string, string]) => void;
        handleStatusChangeCb: (value: string) => void;
        handleNotServed: (value: boolean | null) => void;
    };
    chatListState: {
        orderDirection: boolean;
        setOrderDirection: (val: boolean) => void;
        usersList: Array<Agent>;
    };
}

function arraysEqual<T>(a: Array<T>, b: Array<T>) {
    if (a === b) return true;
    if (a == null || b == null) return false;
    if (a.length !== b.length) return false;

    for (var i = 0; i < a.length; ++i) {
        if (a[i] !== b[i]) return false;
    }
    return true;
}

const datePickerStyle = {
    background: "#F3F4F7",
    borderRadius: "10px",
    borderWitdth: "10px",
    borderStyle: "solid",
    borderColor: "#F3F4F7",
};

export default function IonFiltersMobile({
    filters,
    handlers,
    chatListState,
}: IonFiltersMobileProps) {
    const { t } = useTranslation();

    //@ts-ignore
    const { actions, appState } = useAppContext();

    const currentAgentGroups = useMemo(() => {
        return appState.agent.chatGroups;
    }, [appState.agent.chatGroups]);

    const currentSelectedGroupsNames = useMemo(() => {
        return (
            filters.chatGroupsId &&
            currentAgentGroups
                .filter((g: any) => filters.chatGroupsId?.includes(g.id))
                .map((g: any) => g.name)
        );
    }, [filters, currentAgentGroups]);

    const conversationLabels: Array<ConversationLabel> = useMemo(() => {
        return appState.conversationLabels;
    }, [appState]);

    const currentSelectedAgentsNames = useMemo(() => {
        if (!filters.participatingAgentsId) return [];

        const agentsFullName = chatListState.usersList
            .filter((u) => filters.participatingAgentsId?.includes(u.id))
            .map((u) => u.email);

        return agentsFullName;
    }, [filters, chatListState]);

    const currentSelectedConvLabelsValue: Array<string> = useMemo(() => {
        if (!filters.conversationLabels) return [];
        return conversationLabels
            .filter((l) => filters.conversationLabels?.includes(l.id))
            .map((l) => l.value);
    }, [filters, conversationLabels]);

    const onSelectGroup = (groupsName: Array<string>) => {
        if (!groupsName || !Array.isArray(groupsName)) return;
        if (arraysEqual(groupsName, currentSelectedGroupsNames)) return;
        const groupsId = currentAgentGroups
            .filter((g: any) => groupsName.includes(g.name))
            .map((g: any) => g.id);
        handlers.handleChangeChatGroup(groupsId);
    };

    const onSelectAgent = (agentsNames: Array<string>) => {
        if (!agentsNames || !Array.isArray(agentsNames)) return;
        if (arraysEqual(currentSelectedAgentsNames, agentsNames)) return;

        const newSelectedAgentsId = chatListState.usersList
            .filter((u) => agentsNames.includes(u.email))
            .map((u) => u.id);

        handlers.handleSelectAgents(newSelectedAgentsId);
    };

    const onSelectConversationLabel = (labelsValue: Array<string>) => {
        if (!labelsValue || !Array.isArray(labelsValue)) return;
        if (arraysEqual(currentSelectedConvLabelsValue, labelsValue)) return;

        const labelsId = conversationLabels
            .filter((l) => labelsValue.includes(l.value))
            .map((l) => l.id);

        handlers.handleSelectConversationLabels(labelsId);
    };

    const onChangeDateRange = (startDate?: string | null, endDate?: string | null) => {
        if (!startDate && !endDate) return;
        const newStartDate = startDate || filters.startDatetime;
        const newEndDate = endDate || filters.endDatetime;

        //dateobj must be not null to make the change, but it's value does not serve
        // any purpouse
        const dateObj = moment();
        handlers.handleDateRangeChange(dateObj, [newStartDate, newEndDate]);
    };

    return (
        <IonList>
            <IonItem>
                <IonLabel>{t("filters.agents")}</IonLabel>
                <IonSelect
                    multiple
                    placeholder={t("filters.selectAgentsPlaceholder")}
                    value={currentSelectedAgentsNames}
                    onIonChange={(e) => onSelectAgent(e.detail.value)}
                >
                    {chatListState.usersList.map((u) => (
                        <IonSelectOption value={u.email}>{u.email}</IonSelectOption>
                    ))}
                </IonSelect>
            </IonItem>

            <IonItem>
                <IonLabel>{t("filters.state")}</IonLabel>
                <IonSelect
                    value={filters.status}
                    placeholder={t("filters.all")}
                    onIonChange={(e) => handlers.handleStatusChangeCb(e.detail.value)}
                >
                    <IonSelectOption value={CTA.FILTERS.ACTIVE}>
                        {t(`filters.${CTA.FILTERS.ACTIVE.toLocaleLowerCase()}`)}
                    </IonSelectOption>
                    <IonSelectOption value={CTA.FILTERS.INIT_BY_AGENT}>
                        {t(`filters.${CTA.FILTERS.INIT_BY_AGENT.toLocaleLowerCase()}`)}
                    </IonSelectOption>
                    <IonSelectOption value={CTA.FILTERS.ENDED}>
                        {t(`filters.${CTA.FILTERS.ENDED.toLocaleLowerCase()}`)}
                    </IonSelectOption>
                </IonSelect>
            </IonItem>

            <IonItem>
                <IonLabel>{t("filters.filterByGroups")}</IonLabel>
                <IonSelect
                    placeholder={t("filters.selectGroupsPlaceholder")}
                    multiple
                    onIonChange={(e) => onSelectGroup(e.detail.value)}
                    value={currentSelectedGroupsNames}
                >
                    {currentAgentGroups?.map((g: any) => (
                        <IonSelectOption>{g.name}</IonSelectOption>
                    ))}
                </IonSelect>
            </IonItem>

            <IonItem>
                <IonLabel>{t("channels")}</IonLabel>
                <IonSelect
                    placeholder={t("filters.selectChannelsPlaceholder")}
                    interface="action-sheet"
                    onIonChange={(e) => handlers.handleChangeChannel(e.detail.value)}
                    selectedText={
                        filters.customerChannel && t(`filters.channels.${filters.customerChannel}`)
                    }
                >
                    <IonSelectOption key={CTA.CHANNELS.WHATSAPP} value={CTA.CHANNELS.WHATSAPP}>
                        {t("whatsapp")}
                    </IonSelectOption>
                    <IonSelectOption key={CTA.CHANNELS.INSTAGRAM} value={CTA.CHANNELS.INSTAGRAM}>
                        {t("instagram")}
                    </IonSelectOption>
                    <IonSelectOption key={CTA.CHANNELS.FACEBOOK} value={CTA.CHANNELS.FACEBOOK}>
                        {t("facebook")}
                    </IonSelectOption>
                    <IonSelectOption
                        key={CTA.CHANNELS.FACEBOOK_COMMENT}
                        value={CTA.CHANNELS.FACEBOOK_COMMENT}
                    >
                        {t("facebookComment")}
                    </IonSelectOption>
                    <IonSelectOption
                        key={CTA.CHANNELS.OPTIWE_CHAT}
                        value={CTA.CHANNELS.OPTIWE_CHAT}
                    >
                        {t("webChat")}
                    </IonSelectOption>
                    <IonSelectOption key={CTA.CHANNELS.SMS} value={CTA.CHANNELS.SMS}>
                        {t("sms")}
                    </IonSelectOption>
                    <IonSelectOption key={null} value={null}>
                        {t("filters.channels.noFilter")}
                    </IonSelectOption>
                </IonSelect>
            </IonItem>

            <IonItem>
                <IonLabel>{t("filters.labels")}</IonLabel>
                <IonSelect
                    placeholder={t("filters.selectTagPlaceholder")}
                    multiple
                    onIonChange={(e) => onSelectConversationLabel(e.detail.value)}
                    value={currentSelectedConvLabelsValue}
                >
                    {conversationLabels.map((l: any) => (
                        <IonSelectOption value={l.value}>{l.value}</IonSelectOption>
                    ))}
                </IonSelect>
            </IonItem>

            {filters.status === "ENDED" && (
                <>
                    <IonItem>
                        <IonLabel>{t("filters.startDate")}</IonLabel>
                        <IonDatetimeButton datetime="datetime-start"></IonDatetimeButton>
                        <IonModal keepContentsMounted={true}>
                            <IonDatetime
                                onIonChange={(e) =>
                                    onChangeDateRange(
                                        e.detail.value as string | undefined | null,
                                        undefined
                                    )
                                }
                                value={moment(filters.startDatetime, moment.ISO_8601).toISOString()}
                                style={datePickerStyle}
                                id="datetime-start"
                                showDefaultButtons
                                max={moment(filters.endDatetime, moment.ISO_8601).toISOString()}
                            ></IonDatetime>
                        </IonModal>
                    </IonItem>

                    <IonItem>
                        <IonLabel>{t("filters.endDate")}</IonLabel>
                        <IonDatetimeButton datetime="datetime-end"></IonDatetimeButton>
                        <IonModal keepContentsMounted={true}>
                            <IonDatetime
                                id="datetime-end"
                                style={datePickerStyle}
                                onIonChange={(e) =>
                                    onChangeDateRange(
                                        undefined,
                                        e.detail.value as string | undefined | null
                                    )
                                }
                                value={moment(filters.endDatetime, moment.ISO_8601).toISOString()}
                                showDefaultButtons
                                min={moment(filters.startDatetime, moment.ISO_8601).toISOString()}
                                max={moment().toISOString()}
                            />
                        </IonModal>
                    </IonItem>

                    <IonItem>
                        <IonLabel>{t("filters.wasNotServed")} </IonLabel>
                        <IonToggle
                            checked={filters.notServed}
                            onIonChange={() =>
                                handlers.handleNotServed(filters.notServed ? null : true)
                            }
                        />
                    </IonItem>
                </>
            )}
            <IonItem>
                <IonLabel>{t("filters.reverse")}</IonLabel>
                <IonToggle
                    checked={chatListState.orderDirection}
                    onIonChange={() =>
                        chatListState.setOrderDirection(!chatListState.orderDirection)
                    }
                />
            </IonItem>

            <IonItem>
                <IonLabel>{t("filters.filterWaiting")}</IonLabel>
                <IonToggle
                    checked={filters.isWaiting}
                    onIonChange={() => handlers.handleWaitingFilter()}
                />
            </IonItem>

            <IonItem>
                <IonLabel>{t("filters.ordering")}</IonLabel>
                <IonSelect
                    placeholder={t("filters.default")}
                    interface="action-sheet"
                    onIonChange={(e) => handlers.handleChangeSortBy(e.detail.value)}
                    value={filters.sortBy}
                >
                    <IonSelectOption value={null}>{t("filters.default")}</IonSelectOption>
                    <IonSelectOption value={CTA.FILTERS.DATE_FIELD_FILTERS.START_WAITING_ON}>
                        {t("filters.START_WAITING_ON")}
                    </IonSelectOption>
                    <IonSelectOption value={CTA.FILTERS.DATE_FIELD_FILTERS.LAST_END_USER_ACTIVITY}>
                        {t("filters.LAST_END_USER_ACTIVITY")}
                    </IonSelectOption>
                </IonSelect>
            </IonItem>
        </IonList>
    );
}
